import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { listGosiRates } from '../api/Payroll/gosi';
import { getApiErrorMessage } from '../utils/apiErrors';
import type { GosiRateScheduleView } from '../models/payroll';

export interface UseGosiRatesResult {
  rates: GosiRateScheduleView[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export const useGosiRates = (): UseGosiRatesResult => {
  const { t } = useTranslation();
  const [rates, setRates] = useState<GosiRateScheduleView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listGosiRates();
      if (result.errors.length === 0) {
        setRates(result.payload ?? []);
      } else {
        setError(getApiErrorMessage(result, t, t('Failed to load GOSI rates')));
      }
    } catch (e) {
      // CO-569: the interceptor rejects with the parsed `Result` body — a plain object, never an
      // `Error` — so `e instanceof Error` was always false and the server reason was dropped.
      setError(getApiErrorMessage(e, t, t('Failed to load GOSI rates')));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { rates, loading, error, refresh };
};
